import React, { createContext, useContext, useReducer, useCallback, useEffect, useRef, useMemo, ReactNode } from 'react';
import { notificationService } from '../services/notificationService';
import { useAppSelector } from '../store';
import {
  fetchInbox,
  fetchUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
  deleteNotification as apiDeleteNotification,
  clearReadNotifications as apiClearReadNotifications,
  type InboxNotification,
  type InboxNotificationType,
  type InboxNotificationCategory,
  type InboxNotificationPriority,
} from '../services/inboxApi';

// Enhanced notification interface
// Phase C.89: type/category/priority unions imported from inboxApi
// rather than hand-redeclared. After Phase C.88 made inboxApi the
// single source of truth via const tuples, every other consumer
// should derive from it — including the UI reducer's row shape.
// Phase C.6 (include 'infra' / 'ssr') is preserved automatically
// because the imported union already covers them.
export interface Notification {
  id: string;
  type: InboxNotificationType;
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
  priority: InboxNotificationPriority;
  category: InboxNotificationCategory;
  jobId?: string;
  projectId?: string;
  // Phase C.62: `userId` and `persistent` removed from UI type —
  // they were carried over from a legacy schema sketch but never
  // read by any component. The current viewer is implicit (inbox is
  // already user-scoped) and "persistence" is handled by reducer
  // dedup + DB TTL, not a per-row flag.
  autoHide?: boolean;
  // Phase C.76: `duration` removed — no caller ever set a custom
  // value, all autoHide toasts used the 5000ms default. Hardcoded
  // in the autohide setTimeout below and in Toast.tsx progress bar.
  actions?: Array<{
    label: string;
    action: () => void;
    variant?: 'primary' | 'secondary' | 'danger';
  }>;
  // Phase C.28: server-pushed notifications carry a plain URL so the
  // render layer can decide between react-router navigate (internal
  // paths starting with "/") and window.open (external links). The
  // legacy `actions` closures still work — `actionUrl` is the
  // preferred path for new inbox rows.
  actionUrl?: string;
  actionLabel?: string;
  metadata?: Record<string, any>;
}

// Notification preferences
//
// Phase C.63: `enableToasts` and `enableInApp` removed — they were
// boolean defaults sitting in state but no consumer ever gated on
// them. Toast rendering is unconditional (filtered by
// autoHide/priority/read), in-app rendering is the only path.
// Re-add as toggles only if the UI gains a true "hide everything
// in-app" mode.
// Phase C.108: `categories` and `priorities` Record fields removed
// too. Phase C.107 dropped the push-time guard that read them, and
// they were the only consumer. Re-add (one-line per field, plus
// `allTrue(tuple)` for defaults) when a real category-mute toggle
// lands that filters consistently across ADD/LOAD/COUNT paths.
export interface NotificationPreferences {
  enableSound: boolean;
  quietHours: {
    enabled: boolean;
    start: string; // HH:mm format
    end: string;   // HH:mm format
  };
}

// Phase C.81: single source of truth for the sticky banner shape.
// `NotificationState` holds it, `SystemAlertBanner` consumes it —
// both used to declare identical inline types and drifted at the
// last edit (Phase C.72 had to delete `persistent` in two places).
// Phase C.35 deep-link fields are optional and lifted from the
// source notification when Phase C.12 promotes a critical row.
export interface SystemAlertPayload {
  id: string;
  type: 'info' | 'warning' | 'error';
  title: string;
  message: string;
  dismissible: boolean;
  actionUrl?: string;
  actionLabel?: string;
}

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  preferences: NotificationPreferences;
  isConnected: boolean;
  systemAlert?: SystemAlertPayload;
  // Phase B.3: cursor for paginated "load more". null = no more rows
  // or initial load hasn't run yet.
  nextCursor: string | null;
  loadingMore: boolean;
  // Phase C.26: timestamp bumped only when ADD_NOTIFICATION actually
  // adds a fresh row (post-dedup). Bell animation watches this so
  // server-resync count bumps (LOAD_NOTIFICATIONS / SET_UNREAD_COUNT)
  // don't trigger ringing-bell false positives.
  lastArrivalAt: number;
}

type NotificationAction =
  | { type: 'ADD_NOTIFICATION'; payload: Notification }
  | { type: 'REMOVE_NOTIFICATION'; payload: string }
  | { type: 'MARK_AS_READ'; payload: string }
  | { type: 'MARK_ALL_AS_READ'; payload?: { category?: InboxNotificationCategory } }
  | { type: 'CLEAR_ALL'; payload?: { category?: InboxNotificationCategory } }
  | { type: 'UPDATE_PREFERENCES'; payload: Partial<NotificationPreferences> }
  | { type: 'SET_CONNECTION_STATUS'; payload: boolean }
  // Phase C.79: `SET_SYSTEM_ALERT` removed — banner state is driven
  // entirely by reducer side-effects (Phase C.12 promotes critical
  // ADD_NOTIFICATION, MARK_*/REMOVE_*/CLEAR_* auto-clear when the
  // banner row is touched, DISMISS_SYSTEM_ALERT for manual X).
  // No external setter remained after C.78 pruned the ImportMonitor
  // test button.
  | { type: 'DISMISS_SYSTEM_ALERT' }
  // Phase B.2: server-loaded inbox replaces local state on mount /
  // refresh. Carries an authoritative unreadCount from the backend so
  // the bell badge is correct even when the loaded page is partial.
  | { type: 'LOAD_NOTIFICATIONS'; payload: { items: Notification[]; unreadCount: number; nextCursor: string | null } }
  | { type: 'APPEND_NOTIFICATIONS'; payload: { items: Notification[]; nextCursor: string | null } }
  | { type: 'SET_LOADING_MORE'; payload: boolean }
  // Phase C.59: batched siblings for the per-id MARK_AS_READ and
  // REMOVE_NOTIFICATION actions. Phase C.38 multi-tab sync can
  // arrive with hundreds of ids in one event (e.g. sibling-tab
  // markAllRead) — looping per-id dispatch causes one re-render
  // per id. Batched dispatches handle the set in a single render.
  | { type: 'MARK_MANY_AS_READ'; payload: { ids: string[] } }
  | { type: 'REMOVE_MANY_NOTIFICATIONS'; payload: { ids: string[] } }
  // Phase C.11: authoritative resync from server. Multi-tab + TTL
  // deletion + cross-device actions all desync the local count;
  // this lets a background poll correct it without disturbing the
  // loaded notifications list.
  | { type: 'SET_UNREAD_COUNT'; payload: number };

// Phase C.108: `allTrue` helper retired alongside the
// categories/priorities prefs it built defaults for.
const defaultPreferences: NotificationPreferences = {
  enableSound: true,
  quietHours: {
    enabled: false,
    start: '22:00',
    end: '08:00',
  },
};

const initialState: NotificationState = {
  notifications: [],
  unreadCount: 0,
  preferences: defaultPreferences,
  isConnected: false,
  systemAlert: undefined,
  nextCursor: null,
  loadingMore: false,
  lastArrivalAt: 0,
};

/**
 * Convert a backend `InboxNotification` row into the UI `Notification`
 * shape used by the reducer. Category passes through 1:1 now that the
 * UI type recognizes 'infra' and 'ssr' (Phase C.6).
 */
function inboxToNotification(row: InboxNotification): Notification {
  return {
    id: row._id,
    type: row.type,
    title: row.title,
    message: row.message,
    timestamp: row.createdAt,
    read: row.read,
    priority: row.priority,
    category: row.category,
    projectId: row.projectId,
    jobId: row.jobId,
    metadata: row.metadata as Record<string, any> | undefined,
    // Phase C.28: pass through actionUrl/actionLabel raw so the
    // render layer can do react-router navigate for internal paths
    // (starting with "/") instead of forcing a new tab.
    actionUrl: row.actionUrl,
    actionLabel: row.actionLabel,
  };
}

/**
 * Phase C.6: backend row IDs are 24-char hex (Mongoose ObjectId).
 * Client-generated toast IDs (`notification-${Date.now()}-${random}`)
 * don't exist on the server, so any API mutation against them is
 * wasted work + a noisy 400. Use this guard before round-tripping.
 */
function isPersistedId(id: string): boolean {
  return /^[0-9a-fA-F]{24}$/.test(id);
}

function notificationReducer(state: NotificationState, action: NotificationAction): NotificationState {
  switch (action.type) {
    case 'ADD_NOTIFICATION': {
      const notification = action.payload;

      // Phase B.2: dedup by id. Server push via socket may race with
      // initial REST fetch; without this we'd render a row twice.
      if (state.notifications.some((n) => n.id === notification.id)) {
        return state;
      }

      // Phase C.107: the prefs.categories / prefs.priorities push-time
      // filter was removed. It silently dropped live socket pushes
      // for "disabled" categories, but `LOAD_NOTIFICATIONS` and
      // `APPEND_NOTIFICATIONS` had no equivalent filter so the same
      // rows reappeared on refresh — and the periodic
      // SET_UNREAD_COUNT resync counted them either way, so the bell
      // badge desynced from visible state. Runtime category /
      // priority filtering already lives in the NotificationCenter
      // dropdowns (Phase C.104). The Record-shaped prefs entries
      // stay in localStorage (Phase C.90) — keeping the schema
      // ready in case we wire a real category-mute toggle later.

      // Check quiet hours
      if (state.preferences.quietHours.enabled && notification.priority !== 'critical') {
        const now = new Date();
        const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
        const { start, end } = state.preferences.quietHours;

        if ((start <= end && currentTime >= start && currentTime <= end) ||
            (start > end && (currentTime >= start || currentTime <= end))) {
          return state;
        }
      }

      // Phase C.12: critical notifications also surface as a sticky
      // banner via systemAlert so a user mid-flow doesn't miss them
      // just because the bell dropdown is closed. The bell row is
      // still added — the banner is a parallel surface, not a
      // replacement. Banner stays until the user dismisses it; we
      // don't pile new banners on top of older ones (last critical
      // wins, simpler than queueing).
      const nextSystemAlert =
        notification.priority === 'critical'
          ? {
              id: notification.id,
              type:
                notification.type === 'success'
                  ? 'info'
                  : notification.type,
              title: notification.title,
              message: notification.message,
              dismissible: true,
              // Phase C.35: forward the deep link so the banner can
              // route operators straight to the incident / page they
              // need without hunting through the bell list.
              actionUrl: notification.actionUrl,
              actionLabel: notification.actionLabel,
            }
          : state.systemAlert;
      return {
        ...state,
        notifications: [notification, ...state.notifications],
        unreadCount: state.unreadCount + (notification.read ? 0 : 1),
        systemAlert: nextSystemAlert,
        lastArrivalAt: Date.now(),
      };
    }

    case 'LOAD_NOTIFICATIONS': {
      // Server is authoritative — replace local list and trust the
      // backend-provided unreadCount (might be > items.length if the
      // user has more unread rows than the loaded page).
      return {
        ...state,
        notifications: action.payload.items,
        unreadCount: action.payload.unreadCount,
        nextCursor: action.payload.nextCursor,
      };
    }

    case 'APPEND_NOTIFICATIONS': {
      // Dedup against current state so a "load more" that overlaps
      // with anything already loaded (e.g. socket push during the
      // round trip) doesn't render duplicates.
      const existingIds = new Set(state.notifications.map((n) => n.id));
      const fresh = action.payload.items.filter((n) => !existingIds.has(n.id));
      return {
        ...state,
        notifications: [...state.notifications, ...fresh],
        nextCursor: action.payload.nextCursor,
        loadingMore: false,
      };
    }

    case 'SET_LOADING_MORE':
      return { ...state, loadingMore: action.payload };

    case 'SET_UNREAD_COUNT':
      return { ...state, unreadCount: action.payload };

    case 'MARK_MANY_AS_READ': {
      const idSet = new Set(action.payload.ids);
      if (idSet.size === 0) return state;
      let flipped = 0;
      const next = state.notifications.map((n) => {
        if (idSet.has(n.id) && !n.read) {
          flipped += 1;
          return { ...n, read: true };
        }
        return n;
      });
      if (flipped === 0) return state;
      const bannerCleared =
        state.systemAlert && idSet.has(state.systemAlert.id);
      return {
        ...state,
        notifications: next,
        unreadCount: Math.max(0, state.unreadCount - flipped),
        systemAlert: bannerCleared ? undefined : state.systemAlert,
      };
    }

    case 'REMOVE_MANY_NOTIFICATIONS': {
      const idSet = new Set(action.payload.ids);
      if (idSet.size === 0) return state;
      const removedUnread = state.notifications.reduce(
        (acc, n) => (idSet.has(n.id) && !n.read ? acc + 1 : acc),
        0
      );
      const next = state.notifications.filter((n) => !idSet.has(n.id));
      if (next.length === state.notifications.length) return state;
      const bannerCleared =
        state.systemAlert && idSet.has(state.systemAlert.id);
      return {
        ...state,
        notifications: next,
        unreadCount: Math.max(0, state.unreadCount - removedUnread),
        systemAlert: bannerCleared ? undefined : state.systemAlert,
      };
    }

    case 'REMOVE_NOTIFICATION': {
      const notification = state.notifications.find(n => n.id === action.payload);
      // Phase C.58: if the banner's underlying notification just
      // went away, clear the banner too. Without this the sticky
      // critical alert stayed on screen pointing at a row that
      // doesn't exist anymore.
      const bannerCleared = state.systemAlert?.id === action.payload;
      return {
        ...state,
        notifications: state.notifications.filter(n => n.id !== action.payload),
        unreadCount: state.unreadCount - (notification && !notification.read ? 1 : 0),
        systemAlert: bannerCleared ? undefined : state.systemAlert,
      };
    }

    case 'MARK_AS_READ': {
      const notification = state.notifications.find(n => n.id === action.payload);
      if (!notification || notification.read) return state;

      // Phase C.58: marking the banner's notification as read
      // (here or via socket from sibling tab) dismisses the banner.
      // The operator has acknowledged it.
      const bannerCleared = state.systemAlert?.id === action.payload;
      return {
        ...state,
        notifications: state.notifications.map(n =>
          n.id === action.payload ? { ...n, read: true } : n
        ),
        unreadCount: state.unreadCount - 1,
        systemAlert: bannerCleared ? undefined : state.systemAlert,
      };
    }

    case 'MARK_ALL_AS_READ': {
      const scope = action.payload?.category;
      // Phase C.31: optional category scope so "Mark all read" can
      // honor an active filter in NotificationCenter. Without scope
      // behaves like the original mark-everything-read.
      const next = state.notifications.map((n) =>
        !scope || n.category === scope ? { ...n, read: true } : n
      );
      const newUnread = next.filter((n) => !n.read).length;
      // Phase C.58: if the banner's notification is now in the read
      // set, clear the banner. Catches both global mark-all and
      // category-scoped mark-all when the banner category matches.
      const bannerRow = state.systemAlert
        ? next.find((n) => n.id === state.systemAlert!.id)
        : undefined;
      const bannerCleared = !!(bannerRow && bannerRow.read);
      return {
        ...state,
        notifications: next,
        unreadCount: newUnread,
        systemAlert: bannerCleared ? undefined : state.systemAlert,
      };
    }

    case 'CLEAR_ALL': {
      // Phase C.32: match server semantics — only READ rows are
      // hard-removed. Previous behavior wiped every notification
      // locally including unread ones, but the server's
      // `clearRead` only deletes read=true rows, so on next refresh
      // the "cleared" unread notifications would reappear. Misleading.
      // Optional `category` scope mirrors Phase C.31's mark-all-read.
      const scope = action.payload?.category;
      const next = state.notifications.filter((n) => {
        if (!n.read) return true; // unread stays no matter what
        if (scope && n.category !== scope) return true; // out-of-scope stays
        return false; // matches: read + in-scope → drop
      });
      // Phase C.58: if the banner row got dropped, dismiss banner.
      const bannerCleared = !!(
        state.systemAlert &&
        !next.find((n) => n.id === state.systemAlert!.id)
      );
      return {
        ...state,
        notifications: next,
        unreadCount: next.filter((n) => !n.read).length,
        systemAlert: bannerCleared ? undefined : state.systemAlert,
      };
    }

    case 'UPDATE_PREFERENCES':
      return {
        ...state,
        preferences: { ...state.preferences, ...action.payload },
      };

    case 'SET_CONNECTION_STATUS':
      return {
        ...state,
        isConnected: action.payload,
      };

    case 'DISMISS_SYSTEM_ALERT':
      return {
        ...state,
        systemAlert: undefined,
      };

    default:
      return state;
  }
}

interface NotificationContextType {
  // State
  notifications: Notification[];
  unreadCount: number;
  preferences: NotificationPreferences;
  isConnected: boolean;
  systemAlert?: NotificationState['systemAlert'];
  // Phase B.3: pagination state for "load more" in NotificationCenter.
  hasMore: boolean;
  loadingMore: boolean;
  // Phase C.26: timestamp of the last fresh ADD_NOTIFICATION so the
  // bell can animate only on real arrivals (not on server resync).
  lastArrivalAt: number;

  // Actions
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp'>) => void;
  removeNotification: (id: string) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: (params?: { category?: InboxNotificationCategory }) => void;
  clearAll: (params?: { category?: InboxNotificationCategory }) => void;
  loadMore: () => Promise<void>;
  updatePreferences: (preferences: Partial<NotificationPreferences>) => void;
  // Phase C.79: `setSystemAlert` removed from public surface — only
  // the reducer's internal ADD_NOTIFICATION promotion (Phase C.12)
  // sets the banner now. `dismissSystemAlert` stays because the
  // Layout X button calls it.
  dismissSystemAlert: () => void;

  // Utilities
  // Phase C.60: `getNotificationsByCategory` / `getNotificationsByPriority`
  // removed — destructured in NotificationCenter but never actually
  // used. Filtering happens client-side via the dropdowns (category
  // / priority filter state + reducer guards).
  // Phase C.61: `playNotificationSound` removed too — Phase C.25
  // moved sound playback to a centralized observer effect.
  // Phase C.96: `getUnreadNotifications` removed too — NotificationBell
  // was the only consumer, and only for `criticals.length > 0`. Now
  // computes the boolean directly via `notifications.some(...)`,
  // skipping the intermediate filter() allocation.
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: React.ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(notificationReducer, initialState);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  // Phase C.7: track auth state so we can refetch the inbox after a
  // mid-session login and clear it after logout. Provider mounts at
  // the app root above Router so the initial mount effect runs while
  // the user is still logged out — without this dependency the inbox
  // stays empty until full page reload.
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  // Phase C.10: also track the JWT so socket reconnects on refresh.
  // Long sessions rotate the token via `useTokenRefresh`; without
  // reconnecting, the live socket keeps the original JWT until it
  // expires and the server drops the connection.
  const authToken = useAppSelector((s) => s.auth.token);
  const connectedTokenRef = useRef<string | null>(null);
  // Phase C.25: observer pattern for sound playback on new socket-
  // pushed notifications. The `notification` socket handler dispatches
  // straight to the reducer (no toast/sound path like local
  // `addNotification` has), so without this we'd silently grow the
  // bell badge without any audio cue for live arrivals.
  const lastSeenIdsRef = useRef<Set<string>>(new Set());

  // Phase C.45: lazy-singleton AudioContext. The previous version
  // created a fresh `new AudioContext()` on every notification —
  // browsers cap the count (Chrome: ~6 active per origin), so a busy
  // inbox would silently start failing once contexts piled up.
  // Now: one context reused across all plays, plus an explicit
  // `resume()` to satisfy the autoplay-after-user-gesture policy
  // when the first sound fires before the user has clicked anything.
  useEffect(() => {
    let audioContext: AudioContext | null = null;
    const getCtx = (): AudioContext | null => {
      if (audioContext) return audioContext;
      try {
        const Ctor =
          window.AudioContext || (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!Ctor) return null;
        audioContext = new Ctor();
        return audioContext;
      } catch {
        return null;
      }
    };

    const playSound = () => {
      const ctx = getCtx();
      if (!ctx) return;
      // Chrome / Safari may suspend a context created without a
      // user gesture; `resume()` succeeds once the user has clicked
      // anywhere in the page. Fire-and-forget — error is logged.
      if (ctx.state === 'suspended') {
        ctx.resume().catch(() => {
          /* will retry next time */
        });
      }
      try {
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();
        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);
        oscillator.frequency.setValueAtTime(800, ctx.currentTime);
        oscillator.frequency.setValueAtTime(600, ctx.currentTime + 0.1);
        gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
        oscillator.start(ctx.currentTime);
        oscillator.stop(ctx.currentTime + 0.3);
      } catch (error) {
        console.warn('Failed to play notification sound:', error);
      }
    };

    (audioRef as unknown as { current: { play: () => void } }).current = {
      play: playSound,
    };

    return () => {
      // Free the context on unmount (provider lifecycle = app
      // lifecycle, so in practice this only fires during HMR).
      audioContext?.close().catch(() => {
        /* best-effort */
      });
      audioContext = null;
    };
  }, []);

  // Phase C.8: split socket lifecycle into two effects.
  //
  // 1) Listener registration (this effect, [] deps) — runs once for
  //    the lifetime of the provider so we don't double-register or
  //    leak handlers when auth state flips.
  // 2) Connection management (next effect, [isAuthenticated] deps) —
  //    `connect(token)` after login, `disconnect()` after logout.
  useEffect(() => {
    const handleConnect = () => {
      dispatch({ type: 'SET_CONNECTION_STATUS', payload: true });
    };

    const handleDisconnect = () => {
      dispatch({ type: 'SET_CONNECTION_STATUS', payload: false });
    };

    const handleNotification = (notification: Notification) => {
      dispatch({ type: 'ADD_NOTIFICATION', payload: notification });
    };

    // Phase C.38 + C.59: cross-tab inbox state sync. Backend emits
    // these after the actor tab's API call so sibling tabs converge
    // without waiting for periodic resync (Phase C.11). Batched
    // reducers handle the entire `ids` array in one dispatch —
    // markAllRead can ship 500+ ids and per-id dispatch was causing
    // that many re-renders.
    const handleRead = (payload: { ids: string[] }) => {
      dispatch({ type: 'MARK_MANY_AS_READ', payload });
    };
    const handleCleared = (payload: { ids: string[] }) => {
      dispatch({ type: 'REMOVE_MANY_NOTIFICATIONS', payload });
    };

    // Phase C.1: legacy `ssr_alert` handler removed. SSR alerts now
    // flow through `createNotificationsForUsers` in
    // `inboxNotificationService` which persists + emits the unified
    // `notification` event. If you see SSR-specific routing here
    // again, route it through the helper instead.
    // Phase C.19: same for `system_alert` — banner is driven by
    // ADD_NOTIFICATION promoting `priority === 'critical'` into
    // `state.systemAlert` (see Phase C.12).

    notificationService.on('connect', handleConnect);
    notificationService.on('disconnect', handleDisconnect);
    notificationService.on('notification', handleNotification);
    notificationService.on('notification:read', handleRead);
    notificationService.on('notification:cleared', handleCleared);

    dispatch({ type: 'SET_CONNECTION_STATUS', payload: notificationService.isConnected() });

    return () => {
      notificationService.off('connect', handleConnect);
      notificationService.off('disconnect', handleDisconnect);
      notificationService.off('notification', handleNotification);
      notificationService.off('notification:read', handleRead);
      notificationService.off('notification:cleared', handleCleared);
    };
  }, []);

  // Phase C.8 + C.10: connect/disconnect socket in lock-step with
  // auth, AND reconnect when the JWT rotates. Without this the
  // socket holds the original token forever — eventually the server
  // drops the connection when the JWT expires and the user loses
  // live push silently.
  useEffect(() => {
    if (!isAuthenticated || !authToken) {
      if (notificationService.isConnected()) {
        notificationService.disconnect();
      }
      connectedTokenRef.current = null;
      return;
    }
    // Already connected with the same token — nothing to do.
    if (
      notificationService.isConnected() &&
      connectedTokenRef.current === authToken
    ) {
      return;
    }
    // Either not connected, or connected with a stale token. Force a
    // clean reconnect so the new JWT lands in the handshake auth.
    if (notificationService.isConnected()) {
      notificationService.disconnect();
    }
    connectedTokenRef.current = authToken;
    notificationService.connect(authToken).catch((error) => {
      console.error('Failed to connect notification service:', error);
      connectedTokenRef.current = null;
    });
  }, [isAuthenticated, authToken]);

  // Load preferences from localStorage. Phase C.108: the
  // categories/priorities deep-merge from Phase C.6 retired
  // alongside the now-pruned Record prefs fields. The remaining
  // preferences (`enableSound`, `quietHours`) are flat scalars
  // for which the plain `parsed` is already a valid Partial.
  // Legacy stored entries with `categories: {...}` keys are
  // tolerated — `UPDATE_PREFERENCES` ignores keys not in the type.
  useEffect(() => {
    const savedPreferences = localStorage.getItem('notification_preferences');
    if (savedPreferences) {
      try {
        const parsed = JSON.parse(savedPreferences) as Partial<NotificationPreferences>;
        dispatch({ type: 'UPDATE_PREFERENCES', payload: parsed });
      } catch (error) {
        console.error('Failed to load notification preferences:', error);
      }
    }
  }, []);

  // Phase C.25: play sound + bell-ring animation hook for new
  // notifications, regardless of whether they came from the socket
  // (handleNotification → dispatch) or the local `addNotification`
  // helper (which already has its own sound + autoHide logic).
  // Diffing by id-set keeps the rule clean: any id we haven't seen
  // before AND that arrived as unread + non-low gets the sound.
  useEffect(() => {
    const knownIds = lastSeenIdsRef.current;
    const fresh: Notification[] = [];
    const nextIds = new Set<string>();
    for (const n of state.notifications) {
      nextIds.add(n.id);
      if (!knownIds.has(n.id) && !n.read && n.priority !== 'low') {
        fresh.push(n);
      }
    }
    lastSeenIdsRef.current = nextIds;
    if (knownIds.size === 0) return; // initial load — don't sound-spam
    if (fresh.length === 0) return;
    if (!state.preferences.enableSound) return;
    if (audioRef.current) {
      try {
        (audioRef.current as any).play();
      } catch (err) {
        console.warn('Failed to play notification sound:', err);
      }
    }
  }, [state.notifications, state.preferences.enableSound]);

  // Phase C.11: keep the bell unread count fresh. Phase C.55:
  // relaxed from 60s → 5 min after Phase C.38 introduced live
  // socket sync for read/cleared state. Multi-tab + cross-device
  // changes now propagate in real time via `notification:read` /
  // `notification:cleared` events, so periodic poll is just a
  // safety net for: stale socket connections, TTL-driven server
  // deletions, missed events. `visibilitychange` listener still
  // forces an immediate resync when the tab gains focus — covers
  // "laptop woken up after hours" without paying a 60s poll cost
  // for the common online case.
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    const syncCount = () => {
      fetchUnreadCount()
        .then((count) => {
          if (cancelled) return;
          dispatch({ type: 'SET_UNREAD_COUNT', payload: count });
        })
        .catch(() => {
          /* best-effort */
        });
    };
    syncCount();
    const interval = window.setInterval(syncCount, 5 * 60 * 1000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') syncCount();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [isAuthenticated]);

  // Phase B.2 + C.7: load persisted inbox when the user becomes
  // authenticated, and wipe local state on logout. Without the auth
  // dependency, a user who logs in after the initial mount would see
  // an empty bell until full page reload — the prototype symptom we
  // explicitly set out to fix. Logout wipes the reducer so the next
  // logged-in user can't briefly see the previous user's inbox.
  useEffect(() => {
    if (!isAuthenticated) {
      dispatch({
        type: 'LOAD_NOTIFICATIONS',
        payload: { items: [], unreadCount: 0, nextCursor: null },
      });
      return;
    }
    let cancelled = false;
    Promise.all([fetchInbox({ limit: 50 }), fetchUnreadCount()])
      .then(([page, unreadCount]) => {
        if (cancelled) return;
        const items = page.items.map(inboxToNotification);
        dispatch({
          type: 'LOAD_NOTIFICATIONS',
          payload: { items, unreadCount, nextCursor: page.nextCursor },
        });
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('Failed to load notification inbox:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  // Save preferences to localStorage
  useEffect(() => {
    localStorage.setItem('notification_preferences', JSON.stringify(state.preferences));
  }, [state.preferences]);

  const addNotification = useCallback((notification: Omit<Notification, 'id' | 'timestamp'>) => {
    // Phase C.15: local toasts (autoHide=true) shouldn't flicker the
    // unread bell badge — they pop in, auto-remove after `duration`,
    // and never represent something the user needs to action. Default
    // them to `read: true` unless the caller is explicit about
    // wanting an unread state. Persistent toasts (autoHide=false,
    // user must dismiss) keep the existing read=false default.
    const isEphemeralToast =
      (notification.autoHide ?? false) && notification.read === undefined;
    const newNotification: Notification = {
      ...notification,
      id: `notification-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
      timestamp: new Date().toISOString(),
      read: isEphemeralToast ? true : (notification.read ?? false),
      priority: notification.priority ?? 'medium',
      category: notification.category ?? 'system',
    };

    dispatch({ type: 'ADD_NOTIFICATION', payload: newNotification });

    // Phase C.25: sound playback moved to centralized observer effect
    // (watches `state.notifications` for fresh ids) so socket-pushed
    // and locally-added notifications both trigger sound consistently.

    // Auto-hide if specified
    if (newNotification.autoHide) {
      setTimeout(() => {
        dispatch({ type: 'REMOVE_NOTIFICATION', payload: newNotification.id });
      }, 5000);
    }
    // Phase C.69: empty dep array — callback body no longer reads
    // `enableSound` (Phase C.25 moved sound to observer effect) and
    // `dispatch` is stable across renders. Stale dep was forcing
    // unnecessary re-memo on every preference change.
  }, []);

  const removeNotification = useCallback((id: string) => {
    // Optimistic: update UI first, then sync server. Server failure
    // is logged but doesn't roll back — the row stays gone locally,
    // worst case it reappears on next page reload.
    dispatch({ type: 'REMOVE_NOTIFICATION', payload: id });
    if (isPersistedId(id)) {
      apiDeleteNotification(id).catch((err) => {
        console.warn('Failed to delete notification on server:', err);
      });
    }
  }, []);

  const markAsRead = useCallback((id: string) => {
    dispatch({ type: 'MARK_AS_READ', payload: id });
    if (isPersistedId(id)) {
      markNotificationRead(id).catch((err) => {
        console.warn('Failed to mark notification read on server:', err);
      });
    }
  }, []);

  // Phase C.105: callsite typed against `InboxNotificationCategory`
  // directly (Phase C.89 made the inboxApi tuple the SoT; Phase
  // C.70's old worry about `Notification['category']` widening
  // retired in C.105 along with the indirect type access).
  const markAllAsRead = useCallback(
    (params?: { category?: InboxNotificationCategory }) => {
      dispatch({ type: 'MARK_ALL_AS_READ', payload: params });
      markAllNotificationsRead(
        params?.category ? { category: params.category } : {}
      ).catch((err) => {
        console.warn('Failed to mark all read on server:', err);
      });
    },
    []
  );

  const clearAll = useCallback(
    (params?: { category?: InboxNotificationCategory }) => {
      // Phase C.32: local reducer mirrors server semantics — only
      // `read: true` rows removed. Optional category scope matches
      // the active filter in NotificationCenter.
      dispatch({ type: 'CLEAR_ALL', payload: params });
      apiClearReadNotifications(
        params?.category ? { category: params.category } : {}
      ).catch((err) => {
        console.warn('Failed to clear read notifications on server:', err);
      });
    },
    []
  );

  const updatePreferences = useCallback((preferences: Partial<NotificationPreferences>) => {
    dispatch({ type: 'UPDATE_PREFERENCES', payload: preferences });
  }, []);

  const dismissSystemAlert = useCallback(() => {
    dispatch({ type: 'DISMISS_SYSTEM_ALERT' });
  }, []);

  const loadMore = useCallback(async (): Promise<void> => {
    if (!state.nextCursor || state.loadingMore) return;
    dispatch({ type: 'SET_LOADING_MORE', payload: true });
    try {
      const page = await fetchInbox({ limit: 50, before: state.nextCursor });
      dispatch({
        type: 'APPEND_NOTIFICATIONS',
        payload: {
          items: page.items.map(inboxToNotification),
          nextCursor: page.nextCursor,
        },
      });
    } catch (err) {
      console.warn('Failed to load more notifications:', err);
      dispatch({ type: 'SET_LOADING_MORE', payload: false });
    }
  }, [state.nextCursor, state.loadingMore]);

  // Memoize the context value to prevent unnecessary re-renders
  const value: NotificationContextType = useMemo(() => ({
    // State
    notifications: state.notifications,
    unreadCount: state.unreadCount,
    preferences: state.preferences,
    isConnected: state.isConnected,
    systemAlert: state.systemAlert,
    hasMore: state.nextCursor !== null,
    loadingMore: state.loadingMore,
    lastArrivalAt: state.lastArrivalAt,

    // Actions
    addNotification,
    removeNotification,
    markAsRead,
    markAllAsRead,
    clearAll,
    loadMore,
    updatePreferences,
    dismissSystemAlert,
  }), [
    state.notifications,
    state.unreadCount,
    state.preferences,
    state.isConnected,
    state.systemAlert,
    state.nextCursor,
    state.loadingMore,
    state.lastArrivalAt,
    addNotification,
    removeNotification,
    markAsRead,
    markAllAsRead,
    clearAll,
    loadMore,
    updatePreferences,
    dismissSystemAlert,
  ]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
